import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PenLine, PenTool, Upload, Trash2, Check, Loader2 } from "lucide-react";

// Authorized-signature pad for the Company Profile. THREE modes — DRAW (canvas), TYPE (script font
// rasterized), UPLOAD (PNG/JPG photo/scan). Whatever the mode, the adopted result is a BLACK signature
// image DATA URL saved via POST /profile/esignature { value, name, title } (the same call draw used), so it
// persists on the profile and flows through sign-all to every signature field. Signing != sending.
// Type/Upload logic for the profile e-signature adopt flow. Signatures are BLACK.
export default function SignaturePad({ current, signerName, signerTitle, isAdmin, onAdopted }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [name, setName] = useState(signerName || "Nalen Williams");
  const [title, setTitle] = useState(signerTitle || "Chief Executive Officer");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(current?.value ? "view" : "draw");
  const [subMode, setSubMode] = useState("draw"); // draw | type | upload
  const [uploadUrl, setUploadUrl] = useState("");

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || mode !== "edit" || subMode !== "draw") return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height); // transparent background -> BLACK ink renders clean on the form
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#000";
    hasInk.current = false;
  }, [mode, subMode]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); hasInk.current = true; };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasInk.current = false; };

  // TYPE mode removed (dir 57B): a typed name is not a signature — it belongs only in printed-name fields.
  // Count real ink in a data-URL image (non-transparent, non-near-white pixels) — a blank canvas => 0.
  const imageInkCount = (dataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas"); c.width = img.width; c.height = img.height;
        const ctx = c.getContext("2d"); ctx.drawImage(img, 0, 0);
        const d = ctx.getImageData(0, 0, c.width, c.height).data;
        let n = 0;
        for (let i = 0; i < d.length; i += 4) {
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          if (d[i + 3] > 40 && lum < 232) n++;
        }
        resolve(n);
      } catch { resolve(1); } // tainted canvas — let the backend be the authority
    };
    img.onerror = () => resolve(0);
    img.src = dataUrl;
  });

  // UPLOAD -> knock out the white paper to transparent and force the ink to solid BLACK, so a phone photo
  // of a pen signature renders as a clean black signature on the form (not a grey photo box).
  const processToBlackTransparent = (srcDataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const maxW = 800; const scale = img.width > maxW ? maxW / img.width : 1;
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.width * scale)); c.height = Math.max(1, Math.round(img.height * scale));
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, c.width, c.height);
        const id = ctx.getImageData(0, 0, c.width, c.height); const d = id.data;
        for (let i = 0; i < d.length; i += 4) {
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          if (d[i + 3] < 10 || lum > 180) { d[i + 3] = 0; }         // transparent / near-white paper -> transparent
          else { d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 255; } // ink -> solid BLACK
        }
        ctx.putImageData(id, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch (e) { resolve(srcDataUrl); } // e.g. tainted canvas — fall back to the raw upload
    };
    img.onerror = () => resolve(srcDataUrl);
    img.src = srcDataUrl;
  });

  const onUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/^image\/(png|jpe?g)$/i.test(f.type)) { toast.error("Upload a PNG or JPG image."); return; }
    const rd = new FileReader();
    rd.onload = async () => { const clean = await processToBlackTransparent(rd.result); setUploadUrl(clean); };
    rd.readAsDataURL(f);
  };

  const adopt = async () => {
    let dataUrl = "";
    if (subMode === "draw") { if (!hasInk.current) { toast.error("Draw your signature first — the canvas is empty."); return; } dataUrl = canvasRef.current.toDataURL("image/png"); }
    else { if (!uploadUrl) { toast.error("Choose a PNG/JPG signature image."); return; } dataUrl = uploadUrl; }
    // dir 57B Part 1a — a signature must be a REAL image with ink; reject a blank/near-empty canvas locally
    // before it can clobber an existing signature (the backend enforces the same rule).
    if ((await imageInkCount(dataUrl)) < 40) { toast.error("The signature is empty — draw or upload a signature with ink."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/profile/esignature", { value: dataUrl, name, title });
      toast.success(`Signature adopted for ${data.signer}. Signing never sends a bid.`);
      setMode("view"); onAdopted?.(dataUrl);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to adopt signature"); }
    finally { setBusy(false); }
  };

  const hasImage = current?.value?.startsWith?.("data:");
  const TABS = [["draw", PenTool, "Draw"], ["upload", Upload, "Upload"]];

  return (
    <div data-testid="signature-pad">
      <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 mb-3">
        <PenLine className="h-3.5 w-3.5 text-gold-dark shrink-0" />
        Draw, type, or upload — applied BLACK to the offer form (SF-1442 20b) and every signature field. Signing does not send.
      </div>

      {mode === "view" && (
        <div className="space-y-3" data-testid="signature-current">
          <div className="border border-zinc-200 rounded-md bg-white p-3 flex items-center justify-center h-28">
            {hasImage ? (
              <img src={current.value} alt="Adopted signature" className="max-h-20" data-testid="signature-image" />
            ) : (
              <span className="text-2xl text-zinc-800" style={{ fontFamily: "'Segoe Script','Brush Script MT',cursive" }}>{current?.value || name}</span>
            )}
          </div>
          <div className="text-[12px] text-zinc-600">
            <b>{current?.name || name}</b>, {current?.title || title}
            {current?.adopted_at ? ` · adopted ${String(current.adopted_at).slice(0, 10)}` : ""}
            {!hasImage && <span className="ml-1 text-amber-600">(typed — draw or upload a real signature)</span>}
          </div>
          {isAdmin && (
            <button onClick={() => { setMode("edit"); setSubMode("draw"); setUploadUrl(""); }} data-testid="signature-redraw-btn"
              className="text-[11px] flex items-center gap-1 text-zinc-700 hover:text-zinc-900 border border-zinc-300 rounded px-2.5 py-1">
              <PenLine className="h-3 w-3" /> {hasImage ? "Re-adopt signature" : "Adopt signature"}
            </button>
          )}
        </div>
      )}

      {mode === "edit" && (
        <div className="space-y-3" data-testid="signature-draw">
          <div className="grid grid-cols-2 gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Signer name" data-testid="signature-name"
              className="border border-zinc-300 rounded px-2 py-1 text-sm outline-none focus:border-gold-dark" />
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Signer title" data-testid="signature-title"
              className="border border-zinc-300 rounded px-2 py-1 text-sm outline-none focus:border-gold-dark" />
          </div>
          <div className="flex gap-1 border-b border-zinc-200">
            {TABS.map(([k, Icon, label]) => (
              <button key={k} data-testid={`sig-mode-${k}`} onClick={() => setSubMode(k)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] border-b-2 -mb-px ${subMode === k ? "border-gold-dark text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {subMode === "draw" && (
            <canvas ref={canvasRef} width={440} height={120}
              className="w-full border-2 border-dashed border-zinc-300 rounded-md bg-white touch-none cursor-crosshair"
              data-testid="signature-canvas"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
          )}
          {subMode === "upload" && (
            <div className="border border-dashed border-zinc-300 rounded-md p-4 text-center bg-white">
              <input type="file" accept="image/png,image/jpeg" onChange={onUpload} className="text-sm" data-testid="signature-upload-input" />
              <div className="text-[11px] text-zinc-400 mt-1">PNG/JPG — white background is knocked out and ink rendered BLACK.</div>
              {uploadUrl && <img src={uploadUrl} alt="signature preview" className="mt-3 max-h-20 mx-auto" data-testid="signature-upload-preview" />}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button onClick={adopt} disabled={busy} data-testid="signature-adopt-btn"
              className="flex items-center gap-1.5 text-[12px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-gold" />} Adopt signature
            </button>
            {subMode === "draw" && (
              <button onClick={clear} data-testid="signature-clear-btn"
                className="flex items-center gap-1.5 text-[12px] text-zinc-600 hover:text-zinc-900 border border-zinc-300 rounded-md px-3 py-1.5">
                <Trash2 className="h-3.5 w-3.5" /> Clear
              </button>
            )}
            {current?.value && (
              <button onClick={() => setMode("view")} data-testid="signature-cancel-btn"
                className="text-[12px] text-zinc-400 hover:text-zinc-700 ml-auto">Cancel</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
